import type { DateTime } from 'luxon';
import type { CustomFieldBase } from './custom-fields';
import type { TimeslotMinute } from './openingHour';
import type { BookingPayment } from './payment';

export interface BookingItem {
	quantity: string;
	item: string | null;
	price: string;
	total: string;
}

export enum BookingStatus {
	Booked = 'booked',
	ToBeConfirmed = 'to_be_confirmed',
	Confirmed = 'confirmed',
	ToBeReconfirmed = 'to_be_reconfirmed',
	Canceled = 'canceled',
	NoShow = 'no_show',
	Left = 'left',
	Intent = 'intent',
	ToBePaid = 'to_be_paid',
	Expired = 'expired'
}

export type SessionInstance = TimeslotMinute & {
	sessionInstanceId: string;
	date: DateTime;
	sessionId: string;
	areaId: string;
};

export type BookingSession = {
	id: string;
	sessionInstanceId: string | null;
	sessionId: string;
	pax: number;
};

export type OrderItemPrice = {
	priceId: string | null;
	quantity: number;
	unitPrice: number;
};

export type OrderOffer = {
	offerId: string;
	prices: Array<OrderItemPrice>;
};

export type Order = {
	offers: Array<OrderOffer>;
	discountAmount: number;
	paid: number;
};

export type BookingWithoutCustomer = Omit<Booking, 'customerId'>;

export enum BookingOrigin {
	Widget = 'widget',
	App = 'app'
}

export enum ReconfirmationStatus {
	NotReconfirmed,
	ReconfirmationSent,
	Reconfirmed
}

export type Booking = {
	id: string;
	date: DateTime;
	order: Order;
	sessions: BookingSession[];
	status: BookingStatus;
	customerRequest: string;
	privateNote: string;
	customerId: string | undefined;
	origin: BookingOrigin;
	unread: boolean;
	pax: number;
	payments: BookingPayment[];
	surveyResult: SurveyResult | null;
	reconfirmationStatus: ReconfirmationStatus;
	customFields: Record<CustomFieldBase['id'], string>;
};

export type SurveyResult = {
	id: number;
	rating: number;
	comment: string;
	answeredAt: DateTime;
	isRead: boolean;
	businessReply: string;
	repliedAt: DateTime | null;
};

export type BookingApi = Omit<Booking, 'customerId'> & {
	customer: Customer | undefined;
	// sessionInstance & bookingSessionId
	sessionInstances: Array<SessionInstance>;
};

export type Customer = {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	phoneOptIn: boolean;
	emailOptIn: boolean;
	bookings: Array<Booking>;
	isVip: boolean;
	firstBookingId: Booking['id'] | null;
	lastBookingId: Booking['id'] | null;
	totalSpent: number;
	tags: Array<string>;
	customFields: Record<CustomFieldBase['id'], string>;
};

export const shortenBookingId = (bookingId: string): string => {
	// Only keep characters before the first dash
	return `#${bookingId.split('-')[0]}`;
};
